import React, { useState, useEffect } from 'react';
import {
  Typography,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  TablePagination,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  InputAdornment,
} from '@mui/material';
import { Search } from '@mui/icons-material';
import { apiService } from '@/services/api';
import { useTranslation } from '@/hooks/useTranslation';

interface OBDCode {
  id: string;
  code: string;
  description: string;
}

const PCodesPage: React.FC = () => {
  const { t } = useTranslation();
  const [codes, setCodes] = useState<OBDCode[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [total, setTotal] = useState(0);
  const [searchQuery, setSearchQuery] = useState<string>('');

  // Load codes from API
  useEffect(() => {
    const loadCodes = async () => {
      try {
        setLoading(true);
        setError(null);

        const response = await apiService.getOBDCodes(
          page + 1,
          rowsPerPage,
          searchQuery || undefined,
        );
        setCodes(response.items);
        setTotal(response.total);
      } catch (err: any) {
        setError(err.message || t('pCodes.errors.failedToLoad'));
        console.error('Error loading OBD codes:', err);
      } finally {
        setLoading(false);
      }
    };

    // Debounce search query
    const timeoutId = setTimeout(() => {
      loadCodes();
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [page, rowsPerPage, searchQuery]);

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(event.target.value);
    setPage(0); // Reset to first page when searching
  };

  if (loading && codes.length === 0) {
    return (
      <Box
        sx={{
          p: 3,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '400px',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" component="h1" gutterBottom>
        {t('pCodes.title')}
      </Typography>

      <Card sx={{ mt: 3 }}>
        <CardContent>
          {/* Search Input */}
          <Box sx={{ mb: 3 }}>
            <TextField
              fullWidth
              placeholder={t('pCodes.searchPlaceholder')}
              value={searchQuery}
              onChange={handleSearchChange}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                ),
              }}
            />
          </Box>

          {error && (
            <Alert variant="filled" severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          {/* Codes Table */}
          <Box sx={{ width: '100%', overflowX: 'auto', minWidth: 0 }}>
            <TableContainer component={Paper} variant="outlined">
              <Table>
              <TableHead>
                <TableRow>
                  <TableCell>
                    <Typography variant="h6">{t('pCodes.table.code')}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="h6">{t('pCodes.table.description')}</Typography>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {codes.map(code => (
                  <TableRow key={code.id}>
                    <TableCell>{code.code}</TableCell>
                    <TableCell>{code.description}</TableCell>
                  </TableRow>
                ))}
                {codes.length === 0 && !loading && (
                  <TableRow>
                    <TableCell colSpan={2} align="center">
                      <Typography variant="body2" color="text.secondary">
                        {t('pCodes.noCodesFound')}
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          </Box>

          {/* Pagination */}
          <TablePagination
            rowsPerPageOptions={[10, 25, 50]}
            component="div"
            count={total}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </CardContent>
      </Card>
    </Box>
  );
};

export default PCodesPage;

